import { Component, Inject, OnInit, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { LogService } from '@pershing/pershing-core';
import { TradingEntitlements, OrderType, NetxAddDigSig } from '../../../models/common';
import { TradingService } from '../../../services/common/trading-service.service';

@Component({
    selector: 'terms-and-conditions-dialog',
    templateUrl: './terms-and-conditions-dialog.component.html',
    styleUrls: ['./terms-and-conditions-dialog.component.css']
})

/*
 * Dialog to present user with disclaimer / terms for Trailing and/or Stop orders
 * that must be accepted before those order types can be placed. Will be used by
 * both equity and option orders, so the component will be part of common trading.
 */
export class TermsAndConditionsDialog implements OnInit, AfterViewInit {
    tradingEntitlements: TradingEntitlements = this.configData.tradingEntitlements;

    showFractionalTradingDisclaimer: boolean;
    showTrailingOrderDisclaimer: boolean;
    showStopOrderDisclaimer: boolean;
    termsSubmissionInProgress: boolean = false;
    showSuccessMsg: boolean = false;
    showErrorMsg: boolean = false;
    showOkCancelBtns: boolean = false;
    showAcknowledgeBtn: boolean = false;
    userMayAcknowledge: boolean = false;
    agreed: boolean = false;
    confirmLabel: string = 'labels.ok';
    docCatgDesc: string = '';
    // Whereas professional side has the disclaimers hard-coded, retail side will get the disclaimer text from the server.
    trailingStopOrderDisclaimerText: string;
    stopOrderDisclaimerText: string;
    fractionalTradingDisclaimerText: string;

    trailingStopOrderTypes: OrderType[] = [OrderType.TRAILING_STOP_DOLLAR, OrderType.TRAILING_STOP_PCT];

    trailingStopLimitOrderTypes: OrderType[] = [OrderType.TRAILING_STOP_LIMIT_AON, OrderType.TRAILING_STOP_LIMIT_PCT_AON, OrderType.TRAILING_STOP_LIMIT_DOLLAR,
        OrderType.TRAILING_STOP_LIMIT_PCT];

    stopOrderTypes: OrderType[] = [OrderType.STOP, OrderType.STOP_LIMIT];

    constructor(
        private tradingService: TradingService,
        private logService: LogService,
        public dialogRef: MatDialogRef<TermsAndConditionsDialog>,
        @Inject(MAT_DIALOG_DATA) public configData: any) {}

    ngOnInit(): void {
        if ( this.configData?.disclaimersType === 'fractionalTrading' ) {   // fractionalTrading disclaimers
              this.showFractionalTradingDisclaimer = true;
              this.showOkCancelBtns = true;

              this.fractionalTradingDisclaimerText = this.tradingEntitlements?.fractionalDollarBasedOrderDisclosureText;
        } else {  // order type based disclaimers
          if ( !!this.configData && !!this.configData.tradingEntitlements ) {
              this.tradingEntitlements = this.configData.tradingEntitlements;
              let orderType: OrderType = this.configData.orderType as OrderType;

              this.showTrailingOrderDisclaimer = (this.trailingStopOrderTypes.indexOf(orderType) > -1 || this.trailingStopLimitOrderTypes.indexOf(orderType) > -1) &&
                  !this.tradingEntitlements.isTrailingOrderAgreementSignedByUser;
              this.showStopOrderDisclaimer = this.stopOrderTypes.indexOf(orderType) > -1 &&
                  !this.tradingEntitlements.isStopOrderAgreementSignedByUser;
              this.showOkCancelBtns = true;
              this.showAcknowledgeBtn = !this.showOkCancelBtns;

            this.trailingStopOrderDisclaimerText = this.tradingEntitlements.tradingDisclaimers['trailingOrderDisclaimer'];
            this.stopOrderDisclaimerText = this.tradingEntitlements.tradingDisclaimers['stopOrderDisclaimer'];
            this.confirmLabel = 'labels.confirm';
          }
        }
    }

    ngAfterViewInit(): void {
        // Use this callback after view init to see if the content region has a scroll bar.
        // If not, user can immediately acknowledge the stop disclosure without having to
        // scroll to the end first.
        let termsContent = document.getElementById('termsContent');
        if ( termsContent.clientHeight >= termsContent.scrollHeight ) {
            this.userMayAcknowledge = true;
        }
    }

    checkScrollDone(event: any) {
        // We use this callback when the dialog content DOES have a scrollbar, so we can
        // confirm the user has scrolled all the way through the content before enabling
        // the acknowledge button. We allow for acknowledgement when the scroll is within
        // 10 pixels of the actual scroll height to allow for any minor variance or rounding
        // in the calculated scroll height.
        //
        // if ( visible height + pixel scrolled >= total height - 10 ), user may now acknowledge
        if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight - 10) {
            this.userMayAcknowledge = true;
        }
    }

    public updateAgreementState(event: MatCheckboxChange): void {
        this.agreed = event.checked;
        this.logService.info('Agreement status changed from ' + !this.agreed + ' to ' + this.agreed);
    }

    public submitTermsAgreementUpdate() {
        this.termsSubmissionInProgress = true;

        let agreements: NetxAddDigSig[] = [];
        let key: string = 'docCatgDesc_';
        if ( this.showFractionalTradingDisclaimer ) {
            agreements.push({docId: '36', docCatgDesc: '0001'});
        }

        if ( this.showTrailingOrderDisclaimer ) {
            key = this.tradingEntitlements.isRetailUser ? key + '27' : key + '26';
            this.docCatgDesc = this.tradingEntitlements.tradingDisclaimers[key];
            agreements.push({docId: this.tradingEntitlements.isRetailUser ? '27' : '26', docCatgDesc: this.docCatgDesc});
        }

        if ( this.showStopOrderDisclaimer ) {
            key = key + '30';
            this.docCatgDesc = this.tradingEntitlements.tradingDisclaimers[key];
            agreements.push({docId: '30', docCatgDesc: this.docCatgDesc});
        }

        this.logService.info('Sending ' + agreements.length + ' agreements for update.');

        this.tradingService.updateTermsAndConditionsAcceptance(this.configData.platform, agreements).subscribe(
            success => this.updateTermsAcceptanceSuccess(),
            error => this.updateTermsAcceptanceError()
        );
    }

    updateTermsAcceptanceSuccess() {
        this.logService.info('Got success response on agreement update.');
        this.showOkCancelBtns = false;
        this.showAcknowledgeBtn = false;

        let dlogResult: any = {
            acceptedTrailingOrderAgmt: false,
            acceptedStopOrderAgmt: false,
            acceptedFractionalTradingAgmt: false
        };

        if ( this.showFractionalTradingDisclaimer ) {
            dlogResult.acceptedFractionalTradingAgmt = true;
            this.showFractionalTradingDisclaimer = false;
        }

        if ( this.showStopOrderDisclaimer ) {
            dlogResult.acceptedStopOrderAgmt = true;
            this.showStopOrderDisclaimer = false;
        }

        if ( this.showTrailingOrderDisclaimer ) {
            dlogResult.acceptedTrailingOrderAgmt = true;
            this.showTrailingOrderDisclaimer = false;
        }

        this.dialogRef.close(dlogResult);
    }

    updateTermsAcceptanceError() {
        this.logService.error('Got error response on agreement update.');
        this.showOkCancelBtns = false;
        this.showAcknowledgeBtn = false;
        this.showStopOrderDisclaimer = false;
        this.showTrailingOrderDisclaimer = false;
        this.showErrorMsg = true;
    }


}
